import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { getListadoCliente, updateProyeccionMes, updateTuboNegocio } from '../api/apiConfig';
import { MdReceipt, MdCheckCircle, MdWarning, MdCancel, MdSave, MdEditSquare, MdArrowForward } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import * as XLSX from 'xlsx';

const DetalleEjecutivo = () => {
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const [clientesData, setClientesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nombresMeses, setNombresMeses] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingTuboCell, setEditingTuboCell] = useState(null);
  const [editTuboValue, setEditTuboValue] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const formatNumber = (num) => {
    if (!num) return '$ 0';
    const number = parseFloat(num);
    return `$ ${number.toLocaleString('es-CL')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  const getRowBackgroundColor = (porcentaje) => {
    if (!porcentaje || porcentaje === 'Sin cesiones en SII') return 'transparent';
    const valor = parseFloat(porcentaje);
    if (isNaN(valor)) return 'transparent';
    if (valor > 95) return '#dcfce7'; // verde claro
    if (valor >= 50) return '#dbeafe'; // azul claro
    if (valor >= 25) return '#fef3c7'; // amarillo claro
    return '#fee2e2'; // rojo claro
  };

  const updateProyeccion = async (rut, cliente, proyeccion) => {
    const currentDate = new Date();
    const anio = currentDate.getFullYear();
    const mes = currentDate.getMonth() + 1;
    
    try {
      await updateProyeccionMes(rut, cliente, id, anio, mes, parseFloat(proyeccion));
      // Actualizar el valor en el estado local
      setClientesData(prev => prev.map(item => 
        item.RUT_CLI === rut ? { ...item, MONT_PRO: proyeccion } : item
      ));
    } catch (error) {
      console.error('Error updating proyeccion:', error);
    }
  };

  const handleCellClick = (index, item) => {
    setEditingCell(index);
    setEditValue(item.MONT_PRO || '0');
  };

  const handleCellSave = async (index, item) => {
    await updateProyeccion(item.RUT_CLI, item.CLIENTE, editValue);
    setEditingCell(null);
    setEditValue('');
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const updateTubo = async (rut, cliente, monto) => {
    const currentDate = new Date();
    const anio = currentDate.getFullYear();
    const mes = currentDate.getMonth() + 1;
    const dia = currentDate.getDate();
    
    try {
      await updateTuboNegocio(rut, cliente, id, anio, mes, dia, parseFloat(monto));
      // Actualizar el valor en el estado local
      setClientesData(prev => prev.map(item => 
        item.RUT_CLI === rut ? { ...item, MONTO_TUBO: monto } : item
      ));
    } catch (error) {
      console.error('Error updating tubo:', error);
    }
  };

  const handleTuboCellClick = (index, item) => {
    setEditingTuboCell(index);
    setEditTuboValue(item.MONTO_TUBO || '0');
  };

  const handleTuboCellSave = async (index, item) => {
    await updateTubo(item.RUT_CLI, item.CLIENTE, editTuboValue);
    setEditingTuboCell(null);
    setEditTuboValue('');
  };

  const handleTuboCellCancel = () => {
    setEditingTuboCell(null);
    setEditTuboValue('');
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.CLIENTE, item.RUT_CLI, item.FEC_ULT_OPE, item.STOCK_CIERRE, item.VENC_STOCK, item.MONT_PRO, item.MONTO_TUBO, item.MES_3, item.MES_2, item.MES_1, item.MES_ACTUAL, item.ESTADO_CLAVE, item.PORCENTAJE_INTERFACTOR];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(clientesData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const data = dataToExport.map(item => ({
      'Cliente': item.CLIENTE || '',
      'RUT': item.RUT_CLI || '',
      'Fecha Última Operación': formatDate(item.FEC_ULT_OPE),
      'Stock Cierre ($)': parseFloat(item.STOCK_CIERRE || 0).toLocaleString('es-CL'),
      'Venc. Stock ($)': parseFloat(item.VENC_STOCK || 0).toLocaleString('es-CL'),
      'Proyección ($)': parseFloat(item.MONT_PRO || 0).toLocaleString('es-CL'),
      'Tubo ($)': parseFloat(item.MONTO_TUBO || 0).toLocaleString('es-CL'),
      [`${nombresMeses.MES_3} ($)`]: parseFloat(item.MES_3 || 0).toLocaleString('es-CL'),
      [`${nombresMeses.MES_2} ($)`]: parseFloat(item.MES_2 || 0).toLocaleString('es-CL'),
      [`${nombresMeses.MES_1} ($)`]: parseFloat(item.MES_1 || 0).toLocaleString('es-CL'),
      [`${nombresMeses.MES_ACTUAL} ($)`]: parseFloat(item.MES_ACTUAL || 0).toLocaleString('es-CL'),
      'Estado Clave': item.ESTADO_CLAVE || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Ejecutivo');
    
    XLSX.writeFile(wb, `detalle_ejecutivo_${id}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchClientes = async () => {
      if (!user || !id) return;
      
      try {
        const data = await getListadoCliente(user, id);
        setClientesData(data || []);
        
        // Extraer nombres de meses del primer registro
        if (data && data.length > 0) {
          const firstItem = data[0];
          setNombresMeses({
            MES_3: firstItem.NOMBRE_MES_3 || 'Mes -3',
            MES_2: firstItem.NOMBRE_MES_2 || 'Mes -2', 
            MES_1: firstItem.NOMBRE_MES_1 || 'Mes -1',
            MES_ACTUAL: firstItem.NOMBRE_MES_ACTUAL || 'Mes Actual'
          });
        }
      } catch (error) {
        console.error('Error fetching clientes:', error);
        setClientesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes();
  }, [user, id]);

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Detalle Ejecutivo</h1>
        <p className="text-gray-600">{id}</p>
      </div>

      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
            Listado de Clientes
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({clientesData.length} registros)
            </span>
          </h3>
          {clientesData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {clientesData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={() => setSearchTerm('')} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== clientesData.length ? ` de ${clientesData.length} registros` : ''}</span>}</div>}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8 text-gray-400" />
          </div>
        ) : clientesData.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            No hay datos disponibles
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container" style={{overflowX: 'auto', maxHeight: '600px'}}>
            <table>
              <thead>
                <tr>
                  <th style={{position: 'sticky', left: 0, zIndex: 11, background: 'linear-gradient(135deg, var(--if-primary) 0%, var(--if-primary-dark) 100%)', minWidth: '250px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTE')}>Cliente {getSortIcon('CLIENTE')}</th>
                  <th style={{minWidth: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_CLI')}>RUT Cliente {getSortIcon('RUT_CLI')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FEC_ULT_OPE')}>Fecha Últ. Operación {getSortIcon('FEC_ULT_OPE')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('STOCK_CIERRE')}>Stock Cierre {getSortIcon('STOCK_CIERRE')}</th>
                  <th style={{cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('VENC_STOCK')}>Venc. Stock {getSortIcon('VENC_STOCK')}</th>
                  <th>
                    <div className="flex items-center justify-end gap-1">
                      <span>Proyección del mes</span>
                      <MdEditSquare className="w-6 h-6 text-white" />
                    </div>
                  </th>
                  <th>
                    <div className="flex items-center justify-end gap-1">
                      <span>Tubo del día</span>
                      <MdEditSquare className="w-4 h-4 text-white" />
                    </div>
                  </th>
                  <th>{nombresMeses.MES_ACTUAL}</th>
                  <th>{nombresMeses.MES_3}</th>
                  <th>{nombresMeses.MES_2}</th>
                  <th>{nombresMeses.MES_1}</th>
                  <th>Clave SII</th>
                  <th>Participación IF según SII (3 meses)</th>
                  <th>Detalle Cliente</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr key={index} style={{backgroundColor: getRowBackgroundColor(item.PORCENTAJE_INTERFACTOR)}}>
                    <td style={{position: 'sticky', left: 0, zIndex: 10, backgroundColor: 'white', minWidth: '250px'}}>{item.CLIENTE}</td>
                    <td style={{minWidth: '100px'}}>{item.RUT_CLI}</td>
                    <td>{formatDate(item.FEC_ULT_OPE)}</td>
                    <td>{formatNumber(item.STOCK_CIERRE)}</td>
                    <td>{formatNumber(item.VENC_STOCK)}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap border-r border-gray-200">
                      {editingCell === index ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-1 py-1 text-xs border rounded text-right"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCellSave(index, item);
                              if (e.key === 'Escape') handleCellCancel();
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleCellSave(index, item)}
                            className="text-green-600 text-xs px-1"
                          >
                            <MdSave className="w-3 h-3" />
                          </button>
                          <button
                            onClick={handleCellCancel}
                            className="text-red-600 text-xs px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className="text-gray-900 cursor-pointer hover:bg-gray-100 px-1 py-1 rounded"
                          onClick={() => handleCellClick(index)}
                        >
                          {formatNumber(item.MONT_PRO)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap border-r border-gray-200">
                      {editingTuboCell === index ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={editTuboValue}
                            onChange={(e) => setEditTuboValue(e.target.value)}
                            className="w-20 px-1 py-1 text-xs border rounded text-right"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleTuboCellSave(index, item);
                              if (e.key === 'Escape') handleTuboCellCancel();
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleTuboCellSave(index, item)}
                            className="text-green-600 text-xs px-1"
                          >
                            <MdSave className="w-3 h-3" />
                          </button>
                          <button
                            onClick={handleTuboCellCancel}
                            className="text-red-600 text-xs px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className="text-gray-900 cursor-pointer hover:bg-gray-100 px-1 py-1 rounded"
                          onClick={() => handleTuboCellClick(index, item)}
                        >
                          {formatNumber(item.MONTO_TUBO)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-900 text-right whitespace-nowrap border-r border-gray-200">{formatNumber(item.MES_ACTUAL)}</td>
                    <td className="py-3 px-4 text-gray-900 text-right whitespace-nowrap border-r border-gray-200">{formatNumber(item.MES_3)}</td>
                    <td className="py-3 px-4 text-gray-900 text-right whitespace-nowrap border-r border-gray-200">{formatNumber(item.MES_2)}</td>
                    <td className="py-3 px-4 text-gray-900 text-right whitespace-nowrap border-r border-gray-200">{formatNumber(item.MES_1)}</td>
                    <td className="py-3 px-4 text-center whitespace-nowrap border-r border-gray-200">
                      {item.ESTADO_CLAVE === 'OK' && <MdCheckCircle className="w-6 h-6 text-green-600 mx-auto" title="Ok clave SII" />}
                      {item.ESTADO_CLAVE === 'REVISAR' && <MdWarning className="w-6 h-6 text-yellow-600 mx-auto" title="Revisar clave SII" />}
                      {item.ESTADO_CLAVE === 'SIN CLAVE' && <MdCancel className="w-6 h-6 text-red-600 mx-auto" title="Sin clave SII" />}
                    </td>
                    <td className="py-3 px-4 text-gray-900 text-center whitespace-nowrap">
                      {item.PORCENTAJE_INTERFACTOR === 'Sin cesiones en SII' || isNaN(parseFloat(item.PORCENTAJE_INTERFACTOR)) 
                        ? item.PORCENTAJE_INTERFACTOR 
                        : `${item.PORCENTAJE_INTERFACTOR}%`}
                    </td>
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/detalle-cliente/${item.RUT_CLI}`)}
                        className="btn-primary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px'
                        }}
                        title="Ver detalle cliente"
                      >
                        <MdArrowForward className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleEjecutivo;